import React from 'react';

export interface MiningProgressRingProps {
  size?: number;
  strokeWidth?: number;
  progressPercent: number; // 0 to 100
  colorScheme?: 'gold' | 'cyan' | 'purple' | 'emerald';
  className?: string;
  children?: React.ReactNode;
}

export const MiningProgressRing: React.FC<MiningProgressRingProps> = ({
  size = 120,
  strokeWidth = 6,
  progressPercent = 0,
  colorScheme = 'cyan',
  className = '',
  children,
}) => {
  const radius = (size - strokeWidth * 2) / 2;
  const circumference = 2 * Math.PI * radius;
  const safePercent = Math.min(100, Math.max(0, progressPercent));
  const strokeDashoffset = circumference - (safePercent / 100) * circumference;

  const gradientMap = {
    gold: {
      id: 'ringGradGold',
      stops: (
        <>
          <stop offset="0%" stopColor="#fffbeb" />
          <stop offset="40%" stopColor="#ffd700" />
          <stop offset="100%" stopColor="#d97706" />
        </>
      ),
      glow: 'rgba(255, 215, 0, 0.4)',
    },
    cyan: {
      id: 'ringGradCyan',
      stops: (
        <>
          <stop offset="0%" stopColor="#ecfeff" />
          <stop offset="50%" stopColor="#00f2fe" />
          <stop offset="100%" stopColor="#0284c7" />
        </>
      ),
      glow: 'rgba(0, 242, 254, 0.4)',
    },
    purple: {
      id: 'ringGradPurple',
      stops: (
        <>
          <stop offset="0%" stopColor="#faf5ff" />
          <stop offset="50%" stopColor="#c084fc" />
          <stop offset="100%" stopColor="#7e22ce" />
        </>
      ),
      glow: 'rgba(192, 132, 252, 0.4)',
    },
    emerald: {
      id: 'ringGradEmerald',
      stops: (
        <>
          <stop offset="0%" stopColor="#ecfdf5" />
          <stop offset="50%" stopColor="#10b981" />
          <stop offset="100%" stopColor="#047857" />
        </>
      ),
      glow: 'rgba(16, 185, 129, 0.4)',
    },
  };

  const scheme = gradientMap[colorScheme];

  return (
    <div
      className={`relative inline-flex items-center justify-center select-none ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="transform -rotate-90"
      >
        <defs>
          <linearGradient id={scheme.id} x1="0%" y1="0%" x2="100%" y2="100%">
            {scheme.stops}
          </linearGradient>
        </defs>

        {/* Outer Background Track */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="rgba(255, 255, 255, 0.08)"
          strokeWidth={strokeWidth}
          fill="none"
        />

        {/* Dynamic Glowing Progress Path */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={`url(#${scheme.id})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          fill="none"
          style={{
            transition: 'stroke-dashoffset 0.8s ease-out',
            filter: `drop-shadow(0 0 6px ${scheme.glow})`,
          }}
        />
      </svg>

      {/* Central Content */}
      {children && (
        <div className="absolute inset-0 flex items-center justify-center">
          {children}
        </div>
      )}
    </div>
  );
};
